import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { Logo } from "./Logo";
import { trackEvent } from "../analytics/ga4";
import { scrollToTop } from "../utils/scroll";

export const Footer = () => {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const links = [
    { to: "/leadership", label: t.nav.leadership },
    { to: "/team", label: t.nav.teams },
    { to: "/organisation", label: t.nav.organisation },
    { to: "/strategie", label: t.nav.strategie },
    { to: "/ueber-mich", label: t.nav.about },
  ];
  return (
    <footer data-testid="site-footer" className="bg-deep text-white">
      <div className="mx-auto max-w-[1600px] px-6 py-20 sm:px-12 md:px-16">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <Link
              to="/"
              onClick={(e) => {
                if (pathname === "/") {
                  e.preventDefault();
                  scrollToTop();
                }
              }}
              aria-label="David Frank — Startseite"
              data-testid="footer-logo-link"
              className="inline-flex"
            >
              <Logo />
            </Link>
            <p className="mt-6 max-w-xs text-sm font-light leading-relaxed text-white/60">
              {t.footer.tagline}
            </p>
          </div>
          <div className="md:col-span-3">
            <p className="text-[10px] font-medium uppercase tracking-label text-wave">
              {t.footer.nav}
            </p>
            <ul className="mt-5 space-y-3">
              {links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.slice(1)}`}
                    className="text-sm font-light text-white/70 transition-colors duration-300 hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="text-[10px] font-medium uppercase tracking-label text-wave">
              {t.footer.contact}
            </p>
            <div className="mt-5 space-y-3 text-sm font-light text-white/70">
              <p>
                <a
                  data-testid="footer-email-link"
                  href="mailto:david@davidfrank.at"
                  onClick={() => trackEvent("contact_cta_click", { placement: "footer_email" })}
                  className="transition-colors hover:text-white"
                >
                  david@davidfrank.at
                </a>
              </p>
              <p>
                <a
                  data-testid="footer-phone-link"
                  href="tel:+4368181964809"
                  onClick={() => trackEvent("contact_cta_click", { placement: "footer_phone" })}
                  className="transition-colors hover:text-white"
                >
                  +43 681 81964809
                </a>
              </p>
              <p>{t.contact.location}</p>
              <p>
                <a
                  data-testid="footer-linkedin-link"
                  href="https://www.linkedin.com/in/david-frank-780b501b1/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 transition-colors hover:text-white"
                >
                  LinkedIn
                  <span aria-hidden="true">↗</span>
                </a>
              </p>
            </div>
          </div>
        </div>
        <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 text-[11px] uppercase tracking-[0.2em] text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} David Frank — {t.footer.rights}</p>
          <div className="flex flex-wrap items-center gap-6">
            <Link to="/impressum" data-testid="footer-link-impressum" className="transition-colors hover:text-white/70">
              {t.footer.impressum}
            </Link>
            <Link to="/datenschutz" data-testid="footer-link-datenschutz" className="transition-colors hover:text-white/70">
              {t.footer.datenschutz}
            </Link>
            <button
              type="button"
              data-testid="footer-analytics-settings-button"
              onClick={() => window.dispatchEvent(new Event("analytics-settings"))}
              className="text-left uppercase transition-colors hover:text-white/70"
            >
              {t.footer.analytics}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
